import { useNavigate } from "react-router-dom";
import { Home, Building2, Sparkles, Check } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const serviceIcons = {
  residential: Home,
  commercial: Building2,
  specialized: Sparkles,
};

const serviceImages = {
  residential: "https://images.unsplash.com/photo-1708113388262-17fdf0e21205?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxzcG90bGVzcyUyMGx1eHVyeSUyMGxpdmluZyUyMHJvb20lMjBzdW5saWdodCUyMGludGVyaW9yJTIwZGVzaWdufGVufDB8fHx8MTc3MzM1NTEyOXww&ixlib=rb-4.1.0&q=85",
  commercial: "https://images.unsplash.com/photo-1758630737900-a28682c5aa69?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBpbnRlcmlvciUyMG1vZGVybnxlbnwwfHx8fDE3NzMzNTUxMzB8MA&ixlib=rb-4.1.0&q=85",
  specialized: "https://images.unsplash.com/photo-1737092720524-9140dc372efb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxzaGlueSUyMGhhcmR3b29kJTIwZmxvb3IlMjBjbGVhbiUyMHBvbGlzaCUyMGhvbWV8ZW58MHx8fHwxNzczMzU1MTQzfDA&ixlib=rb-4.1.0&q=85",
};

const Services = ({ services, loading }) => {
  const navigate = useNavigate();

  const handleServiceClick = (serviceId) => {
    navigate(`/services/${serviceId}`);
  };

  if (loading) {
    return (
      <section id="services" className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Skeleton className="h-8 w-32 mx-auto mb-4" />
            <Skeleton className="h-12 w-96 mx-auto mb-4" />
            <Skeleton className="h-6 w-80 mx-auto" />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-96 rounded-3xl" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-16 md:py-24 bg-gradient-to-b from-white to-blue-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-left mb-16">
          <span className="text-[#2563EB] font-medium text-sm uppercase tracking-wider">
            Our Services
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-2 mb-4">
            Cleaning Solutions for
            <br />
            <span className="gradient-text">Every Need</span>
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl">
            From cozy homes to busy offices, we deliver spotless results with
            eco-friendly products and professional care.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const Icon = serviceIcons[service.id] || Sparkles;
            const image = serviceImages[service.id];
            
            return (
              <div
                key={service.id}
                className={`service-card bg-white rounded-3xl overflow-hidden shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] border border-slate-100 group cursor-pointer ${
                  index === 0 ? "lg:row-span-2" : ""
                }`}
                onClick={() => handleServiceClick(service.id)}
                data-testid={`service-card-${service.id}`}
              >
                {/* Image */}
                <div className={`relative overflow-hidden ${index === 0 ? "h-64" : "h-48"}`}>
                  <img
                    src={image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                        <Icon className="w-5 h-5 text-white" strokeWidth={1.5} />
                      </div>
                      <span className="text-white font-semibold text-lg">
                        {service.title}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <p className="text-slate-600 mb-4">{service.description}</p>
                  
                  {/* Features */}
                  <ul className="space-y-2 mb-4">
                    {service.features.slice(0, index === 0 ? 4 : 2).map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                        <Check className="w-4 h-4 text-[#3B82F6]" strokeWidth={2} />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* Price */}
                  <div className="flex items-baseline gap-1 pt-4 border-t border-slate-100">
                    <span className="text-sm text-slate-500">Starting at</span>
                    <span className="text-2xl font-bold text-[#2563EB]">
                      ${service.starting_price}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
